import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import dayjs from 'dayjs';
import { usePolicyControllerSuspendPolicy, usePolicyControllerActivatePolicy } from 'korint-sdk';
import { PauseIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { DeactivatableButton } from '../features/DeactivatableButton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { toast } from '../ui/use-toast';
import { useAssertedParams } from '../../lib/utils';

export function ToggleSuspensionButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { policyId } = useAssertedParams();
  const { policy, mutate } = usePolicyOnDashboard();

  const isSuspended = policy.status === 'POLICY_SUSPENDED';

  const { mutate: suspendPolicy, isPending: isSuspending } = usePolicyControllerSuspendPolicy({
    mutation: {
      onSuccess: async () => {
        await mutate();
        setOpen(false);
      },
      onError: error => {
        console.error('Failed to suspend policy:', error);
        toast({
          variant: 'destructive',
          title: t('toggleSuspensionButton.toast.suspendErrorTitle'),
          description:
            error instanceof Error ? error.message : t('toggleSuspensionButton.toast.genericError'),
        });
      },
    },
  });

  const { mutate: activatePolicy, isPending: isActivating } = usePolicyControllerActivatePolicy({
    mutation: {
      onSuccess: async () => {
        await mutate();
        toast({
          title: t('toggleSuspensionButton.toast.reactivateSuccessTitle'),
          description: t('toggleSuspensionButton.toast.reactivateSuccessDescription'),
        });
        setOpen(false);
      },
      onError: error => {
        console.error('Failed to activate policy:', error);
        toast({
          variant: 'destructive',
          title: t('toggleSuspensionButton.toast.reactivateErrorTitle'),
          description:
            error instanceof Error ? error.message : t('toggleSuspensionButton.toast.genericError'),
        });
      },
    },
  });

  const isMutating = isSuspending || isActivating;

  const onSubmit = () => {
    if (isMutating) return;
    if (!isSuspended) {
      suspendPolicy({ policyId });
    } else {
      const appliedAt = dayjs().add(1, 'day').startOf('day').toISOString();
      activatePolicy({ policyId, data: { appliedAt } });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <DeactivatableButton
          size="icon"
          variant="outline"
          tooltipLabel={
            isSuspended
              ? t('toggleSuspensionButton.tooltip.reactivate')
              : t('toggleSuspensionButton.tooltip.suspend')
          }
          rules={[]}
        >
          <PauseIcon className="h-4 w-4" />
        </DeactivatableButton>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isSuspended
              ? t('toggleSuspensionButton.dialog.titleReactivate')
              : t('toggleSuspensionButton.dialog.titleSuspend')}
          </DialogTitle>
        </DialogHeader>
        {!isSuspended && (
          <DialogDescription>
            {t('toggleSuspensionButton.dialog.descriptionSuspend')}
          </DialogDescription>
        )}
        {isSuspended && (
          <DialogDescription>
            {t('toggleSuspensionButton.dialog.descriptionReactivate')}
          </DialogDescription>
        )}
        <DialogFooter>
          <Button type="submit" onClick={onSubmit} disabled={isMutating}>
            {t('toggleSuspensionButton.dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
